/**
 * SKIN-240 — the image card's meta line, and the shape the review gallery works in.
 *
 * The three facts the design puts under a thumbnail — shot type, target, body region —
 * are NOT in the image metadata blob. That was verified against production (8575
 * records): the blob carries exactly `type`, `localId`, `remotePath`, `qaMetrics`,
 * `qaFlags`, `qualityScore`, `identifiableFeatures`. The three come from the eCRF
 * instead — the `SurveyField` the image hangs off, `image_target`, and `body_map_code`
 * — so the card is built by crossing the blob with the form, never by reading the blob
 * alone.
 */
import { uniqBy } from 'lodash';
// The failure taxonomy is imported, not restated: `retrieve` vs `decode` is a fact about
// the decrypt pipeline and has exactly one home.
import { DecryptFailureStage } from '../../../hooks/useDecryptedFiles';

/** The reviewer's verdict on one image. `null`/absent means not yet assessed. */
export type QcOutcome = 'correct' | 'needs';

/**
 * Where one image's pixels are in the decrypt pipeline.
 *
 * A union rather than a `blobUrl` plus a state flag, so the contradictory combinations —
 * "ready with no bytes", "failed but here is a URL" — cannot be constructed at all. The
 * flat version needs every consumer to re-ask "do we actually have pixels?" with its own
 * `|| !blobUrl` guard, and a consumer that forgets renders a broken `<img>` for a
 * clinical image. SKIN-241 and SKIN-243 build call sites on this shape, so it is worth
 * being unambiguous before they do.
 *
 * Not `DecryptedFile` from the hook, deliberately. That type has no per-file `loading` and
 * structurally cannot: `useDecryptedFiles` publishes its array once, after the whole batch
 * resolves, so one of its entries is never mid-flight. It is also keyed on `localId` where
 * QC is anchored on `encryptedFileUuid`. The per-image producer that lands with SKIN-256
 * returns this union natively, so converging the two is not an adapter waiting to be
 * written — it is a change to the shipped hook, and it belongs to the ticket that measures
 * the decrypt cost (SKIN-257), not to this one.
 */
export type ImageBytes =
    | { state: 'loading' }
    | { state: 'ready'; blobUrl: string }
    | { state: 'failed'; failedAt: DecryptFailureStage; error?: string };

export interface ReviewImage {
    /** `encrypted_file.uuid` — the anchor every QC write uses. */
    encryptedFileUuid: string;
    /** The device-side id the eCRF record points at. */
    localId: string;
    /**
     * The eCRF repeat-group record this image belongs to — the lesion (SKIN-241).
     *
     * A visit is NOT a lesion: production has 306 visits with one, 78 with two or more,
     * and one with 16. This is what groups a lesion's shot set (overview + calibrated
     * close-up + macro, plus side view when raised), what anchors lesion-level feedback,
     * and what makes "incomplete set" computable.
     */
    groupRecordUuid: string;
    /**
     * The eCRF `SurveyField` name this image hangs off — `lesion_image_overview`,
     * `lesion_image_calibrated_closeup`, `lesion_image_macro`, `lesion_image_side_view`.
     * This is what names the shot type AND what scopes the feedback categories, so it
     * is carried raw rather than pre-rendered.
     */
    fieldName: string;
    /** The `image_target` answer, already resolved to its label. Null when unanswered. */
    target: string | null;
    /** The lesion's `body_map_code` answer, already resolved to its label. Null when unanswered. */
    bodyRegion: string | null;
    /** The device's auto-QC score, raw 0-100. Null when the device reported none. */
    qualityScore: number | null;
    /**
     * The authoritative identifiable flag — reflects any correction (SKIN-237).
     * Tri-state on purpose: `null` is "the device said nothing", which is not the same
     * claim as "no", and must not be rendered as one.
     */
    identifiable: boolean | null;
    /** What the device originally called it, frozen. Differs from `identifiable` after a correction. */
    autoIdentifiable: boolean | null;
    /** Where this image's pixels are. */
    bytes: ImageBytes;
}

/**
 * eCRF field name -> translation key suffix for the shot type.
 *
 * The four names are fixed by the SkincAIr eCRF and are what the API resolves
 * `applies_to` against, so they are stable enough to map. An unknown name still has to
 * render — a study that adds a fifth shot must show *something* under its thumbnail
 * rather than a blank — so this returns null and the caller falls back to
 * `humaniseFieldName`.
 */
const IMAGE_TYPE_KEYS: Record<string, string> = {
    lesion_image_overview: 'overview',
    lesion_image_calibrated_closeup: 'calibrated-closeup',
    lesion_image_macro: 'macro',
    lesion_image_side_view: 'side-view',
    // SKIN-618 — SKIN-41's visit-level shot, shown by VisitShotsPanel.
    healthy_skin_image: 'healthy-skin',
};

/** The translation key suffix for a known shot type, or null for an unmapped field. */
export function imageTypeLabelKey(fieldName: string): string | null {
    return IMAGE_TYPE_KEYS[fieldName] ?? null;
}

/**
 * Last-resort label for a shot type this build does not know about:
 * `lesion_image_dermoscopy` -> `Dermoscopy`. The `lesion_image_` prefix is dropped
 * because every image field carries it, so keeping it would add noise to every card.
 */
export function humaniseFieldName(fieldName: string): string {
    const withoutPrefix = fieldName.replace(/^lesion_image_/, '');
    const words = withoutPrefix.split(/[_-]+/).filter(Boolean);
    if (words.length === 0) return fieldName;
    return words.join(' ').replace(/^./, (c) => c.toUpperCase());
}

/**
 * Whether the curtain must cover this image's pixels for this reviewer.
 *
 * Deliberately a separate predicate from "can the reviewer reveal it": an image flagged
 * identifiable is curtained for EVERYONE on arrival, and holding
 * `VIEW_IDENTIFIABLE_IMAGES` only adds the button that lifts it. Collapsing the two
 * would uncover a patient's face the moment a permitted reviewer opened the visit,
 * which is the opposite of what a curtain is for.
 */
export function isCurtained(image: Pick<ReviewImage, 'identifiable'>): boolean {
    return image.identifiable === true;
}

/**
 * The value a correction would set this flag to (SKIN-243) — the flip of what it holds.
 *
 * One definition, because two components need the same answer and would drift: the dialog
 * uses it to state the direction it is offering, the container to build the body it sends.
 * A dialog that said "Yes → No" while the request set `true` would record the opposite of
 * what the reviewer was shown, in an audit log, permanently.
 *
 * `null` — the device recorded no opinion — flips to `true`, like an explicit `false`.
 * Silence is not an assurance that a patient is unidentifiable in their own photograph, so
 * the correction on offer is the one that adds the claim.
 */
export function correctedIdentifiable(current: boolean | null): boolean {
    return current !== true;
}

/**
 * Collapse a visit's eCRF image records down to one entry per photograph.
 *
 * **This is not a tidy-up, it is the difference between a usable screen and an
 * unusable one.** The gallery is built from `survey_record` rows, and in production
 * 8575 of those rows resolve to only 1756 distinct images — 4.9 records per photograph,
 * with 95% of images appearing in more than one submission. Rendering the records
 * would ask the reviewer to grade the same photograph about five times, and since QC
 * feedback is anchored on the image, those five cards would be five views of one
 * verdict fighting each other.
 *
 * The key is `encryptedFileUuid`, not `localId`: the uuid is what every QC write is
 * anchored on, so deduplicating on anything else could still leave two cards pointing
 * at one row.
 *
 * First occurrence wins for ordering, and `number` is assigned after the collapse so
 * the reviewer sees 1..n with no gaps where duplicates were removed.
 */
export function dedupeImagesByFile<T extends { encryptedFileUuid: string }>(records: T[]): T[] {
    return uniqBy(records, (record) => record.encryptedFileUuid);
}

/**
 * Group a visit's images by lesion, collapsing duplicates across the whole visit first and
 * preserving the order they arrive in.
 *
 * The handoff draws lesion feedback once per visit. That is wrong for roughly a fifth of
 * visits — 78 of 384 production visits carry more than one lesion, and one carries 16 — so
 * a single panel would attribute a complaint about one lesion's shot set to all of them.
 * Images hang off the eCRF repeat-group record, and all 8575 production image records
 * carry one across 2874 groups, so the grouping is always available.
 *
 * **The collapse is inside, and across the whole visit, because a photograph does reach more
 * than one group record in production.** Those same figures force it: 2874 group records
 * against only 1756 distinct photographs, and groups cannot outnumber photographs unless
 * some are shared. `computeVisitProgress` counts the whole visit collapsed while the gallery
 * collapses inside each section, so grouping raw records would render such a photograph as
 * two cards while the rail counted it once.
 *
 * First group wins, which is a resolution rather than a rendering trick: `encryptedFileUuid`
 * is the anchor every QC write is addressed to, so one photograph has exactly one verdict and
 * two cards would be two controls over one stored answer. Whether a photograph *should* hang
 * off two lesion records is a data-model question, not a counting one — see SKIN-276.
 */
export function groupImagesByLesion<T extends { encryptedFileUuid: string; groupRecordUuid: string }>(
    images: T[],
): Array<{ groupRecordUuid: string; images: T[] }> {
    const order: string[] = [];
    const byGroup = new Map<string, T[]>();
    // `uniqBy`, so the gallery's own pass over each section stays idempotent.
    for (const image of dedupeImagesByFile(images)) {
        const existing = byGroup.get(image.groupRecordUuid);
        if (existing) {
            existing.push(image);
        } else {
            order.push(image.groupRecordUuid);
            byGroup.set(image.groupRecordUuid, [image]);
        }
    }
    return order.map((groupRecordUuid) => ({
        groupRecordUuid,
        images: byGroup.get(groupRecordUuid) as T[],
    }));
}
